import React, { FC, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { BlueButton, Box } from '~Styles';
import { gql, useQuery } from '@apollo/client';
import { GetLatestReleases, GetLatestReleasesVariables } from './__queries__';
import { RELEASE_TYPE } from '../../../../../__queries__';
import ReleaseNotes from './ReleaseNotes';
import { useButton, useI18n, useWindowSize } from '~Hooks';
import Feature from './Feature';
import CompactFeatureSkeleton from './Loaders/CompactFeatureSkeleton';
import FeatureSkeleton from './Loaders/FeatureSkeleton';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';

// -- trigger deploy
const Wrapper = styled(Box)`
  overflow-y: auto;
  max-width: 600px;
  padding-bottom: 32px;
  padding-top: 8px;

  height: 100%;
  max-height: calc(100vh - 64px - 160px);

  position: relative;

  background-color: var(--colorBlueGray1100);
  border: 1px solid var(--colorBlueGray1000);
  border-radius: 8px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );

  background-image: linear-gradient(
    to bottom,
    transparent calc(100% - 20px),
    var(--colorBlueGray1200) 100%
  );
`;

const LoadMoreButtonContainer = styled(Box)`
  padding: 24px;
  display: flex;
  justify-content: center;
`;

export const GET_LATEST_RELEASES = gql`
  query GetLatestReleases($limit: Int, $lastEvaluatedKey: String) {
    getLatestReleases(limit: $limit, lastEvaluatedKey: $lastEvaluatedKey) {
      lastEvaluatedKey
      items {
        id
        createdAt
        type
        titleLang
        bodyLang
        notes {
          imageUrls
          titleLang
          companyIds
        }
        triggerModal
        imageUrls
      }
    }
  }
`;

/** Component displaying our latest news and updates */
const NewsAndUpdates: FC = () => {
  const { i18n } = useI18n();
  const windowSize = useWindowSize();
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const releasesQuery = useQuery<GetLatestReleases, GetLatestReleasesVariables>(
    GET_LATEST_RELEASES,
    {
      variables: { limit: 25 },
      fetchPolicy: 'cache-and-network',
    },
  );

  const releases = releasesQuery.data?.getLatestReleases.items || [];
  const lastEvaluatedKey = releasesQuery.data?.getLatestReleases.lastEvaluatedKey;

  // Track the previous lastEvaluatedKey to detect when there are no more pages
  const prevLastEvaluatedKeyRef = useRef<string | null>(null);

  const showLoadMore = !!lastEvaluatedKey && lastEvaluatedKey !== prevLastEvaluatedKeyRef.current;

  const handleLoadMore = async () => {
    if (!lastEvaluatedKey || isLoadingMore) return;

    setIsLoadingMore(true);
    prevLastEvaluatedKeyRef.current = lastEvaluatedKey;
    try {
      await releasesQuery.fetchMore({
        variables: {
          limit: 5,
          lastEvaluatedKey,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            getLatestReleases: {
              ...fetchMoreResult.getLatestReleases,
              items: [...prev.getLatestReleases.items, ...fetchMoreResult.getLatestReleases.items],
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const loadMoreButton = useButton({
    onClick: handleLoadMore,
    disabled: isLoadingMore,
  });

  if (releases.length === 0 && !releasesQuery.loading) {
    return null;
  }

  return (
    <Wrapper
      display="flex"
      flexDirection="column"
      gap="small"
      marginTop={windowSize.width < 1168 ? 'none' : 'medium'}>
      {releasesQuery.loading ? (
        <>
          <CompactFeatureSkeleton />
          <CompactFeatureSkeleton />
        </>
      ) : (
        <>
          {releases.map((release) =>
            release.type === RELEASE_TYPE.RELEASE_NOTES ? (
              <ReleaseNotes key={release.id} release={release} />
            ) : (
              <Feature key={release.id} release={release} />
            ),
          )}

          {isLoadingMore && <FeatureSkeleton />}

          {showLoadMore && !isLoadingMore && (
            <LoadMoreButtonContainer>
              <BlueButton {...loadMoreButton}>{i18n('Expense.LoadMore')}</BlueButton>
            </LoadMoreButtonContainer>
          )}

          {!showLoadMore && (
            <Box
              display="flex"
              justifyContent="center"
              fluid
              marginTop="medium"
              marginBottom="small">
              <Paragraph level={4} color="var(--colorBlueGray300)">
                {i18n('NewsAndUpdates.EndReached')}
              </Paragraph>
            </Box>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default NewsAndUpdates;
